import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { LogOut, Calendar, CreditCard, Plus, Video, Gift, Copy, BookOpen, Download, Zap } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";

export default function ClientPortal() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [paying, setPaying] = useState(null);
  const [libraryItems, setLibraryItems] = useState([]);
  const [hasWebAccess, setHasWebAccess] = useState(false);

  const load = async () => {
    const [b, t, lib, web] = await Promise.all([
      api.get("/bookings"), api.get("/payments/transactions"),
      api.get("/portal/library").catch(() => ({ data: [] })),
      api.get("/ronin-box/web-access").catch(() => ({ data: { has_access: false } })),
    ]);
    setBookings(b.data); setTransactions(t.data);
    setLibraryItems(lib.data || []);
    setHasWebAccess(!!web.data?.has_access);
    refresh();
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const pay = async (booking) => {
    setPaying(booking.id);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/payments/checkout/session", {
        package_id: booking.package_id, origin_url: origin, booking_id: booking.id,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setPaying(null);
    }
  };

  const copyReferral = () => {
    if (!user?.referral_code) return;
    const url = `${window.location.origin}/book?package=virtual_40&ref=${user.referral_code}`;
    navigator.clipboard.writeText(url);
    toast.success("Referral link copied. Share it.");
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="client-portal">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#050505]/80 border-b border-[#27272A]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/">
            <BrandMark size="md" />
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-xs text-zinc-500 hidden md:inline">{user?.email}</span>
            <button onClick={async () => { await logout(); navigate("/"); }} className="flex items-center gap-2 text-zinc-400 hover:text-white text-xs uppercase tracking-widest" data-testid="portal-logout">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-2">My Portal</div>
            <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight">Welcome, {user?.name?.split(" ")[0]}.</h1>
          </div>
          <div className="flex items-center gap-3">
            {hasWebAccess && (
              <Button onClick={() => navigate("/ronin-box/app")} variant="outline" className="border-[#FF331F]/50 bg-[#FF331F]/10 text-white hover:bg-[#FF331F]/20 font-bold uppercase tracking-widest rounded-sm" data-testid="launch-web-trainer-btn">
                <Zap className="h-4 w-4 mr-2 text-[#FF331F]" /> Launch web trainer
              </Button>
            )}
            <Button onClick={() => navigate("/book?package=virtual_40")} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm" data-testid="book-new-session-btn">
              <Plus className="h-4 w-4 mr-2" /> Book a session
            </Button>
          </div>
        </div>

        {/* Referral card */}
        <div className="border border-[#FF331F]/40 bg-gradient-to-br from-[#FF331F]/10 to-[#0F0F0F] p-6 mb-8 grid md:grid-cols-3 gap-6 items-center" data-testid="referral-card">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-2">
              <Gift className="h-5 w-5 text-[#FF331F]" />
              <div className="font-display uppercase tracking-widest text-sm">Refer & earn $20</div>
            </div>
            <p className="text-zinc-300 text-sm">Share your code. When a friend books their first paid session, you both get <span className="text-[#FF331F] font-bold">$20 credit</span> off your next session.</p>
            <div className="mt-3 text-xs text-zinc-500 uppercase tracking-widest">Your credit: <span className="text-emerald-400 font-bold">${(user?.referral_credit || 0).toFixed(2)}</span></div>
          </div>
          <div className="space-y-3">
            <div className="bg-[#050505] border border-[#27272A] px-4 py-3 font-display text-2xl tracking-[0.2em] text-center" data-testid="referral-code">{user?.referral_code || "—"}</div>
            <Button onClick={copyReferral} variant="outline" className="w-full border-[#27272A] text-white hover:bg-[#FF331F] hover:border-[#FF331F] uppercase tracking-widest font-bold rounded-sm bg-transparent" data-testid="copy-referral-link-btn">
              <Copy className="h-3.5 w-3.5 mr-2" /> Copy share link
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Bookings */}
          <div className="border border-[#27272A] bg-[#0F0F0F]" data-testid="my-sessions-card">
            <div className="border-b border-[#27272A] px-6 py-4 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-[#FF331F]" />
              <div className="font-display uppercase tracking-widest text-sm">My sessions</div>
            </div>
            <div className="divide-y divide-[#27272A]">
              {bookings.length === 0 ? (
                <div className="p-10 text-center text-zinc-500 text-sm">No sessions yet. Book your first.</div>
              ) : bookings.map((b) => (
                <div key={b.id} className="p-5" data-testid={`my-booking-${b.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-display uppercase">{b.package_name}</div>
                      {b.starts_at ? (
                        <div className="text-xs text-zinc-500 mt-1">{new Date(b.starts_at).toLocaleString()}</div>
                      ) : b.payment_status === "paid" ? (
                        <div className="text-xs mt-1 inline-flex items-center gap-1.5 bg-[#FF331F]/10 border border-[#FF331F]/40 text-[#FF331F] px-2 py-1 uppercase tracking-widest font-bold" data-testid={`pending-schedule-${b.id}`}>
                          <Calendar className="h-3 w-3" /> Coach will contact you to schedule
                        </div>
                      ) : (
                        <div className="text-xs text-zinc-600 mt-1 italic">Awaiting payment</div>
                      )}
                      {b.zoom_link && <a href={b.zoom_link} target="_blank" rel="noreferrer" className="inline-flex items-center text-[#FF331F] text-xs mt-2"><Video className="h-3 w-3 mr-1" /> Join Zoom</a>}
                    </div>
                    <div className="text-right">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${b.payment_status === "paid" ? "bg-emerald-500/10 text-emerald-400" : "bg-zinc-800 text-zinc-400"}`}>{b.payment_status}</span>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-2">{!b.starts_at && b.payment_status === "paid" ? "Awaiting Scheduling" : b.status}</div>
                    </div>
                  </div>
                  {b.payment_status !== "paid" && b.status !== "cancelled" && (
                    <Button onClick={() => pay(b)} disabled={paying === b.id} size="sm" className="mt-3 bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm text-xs" data-testid={`pay-booking-${b.id}`}>
                      {paying === b.id ? "Loading…" : `Pay $${b.amount?.toFixed(2)}`}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Payments */}
          <div className="border border-[#27272A] bg-[#0F0F0F]" data-testid="my-payments-card">
            <div className="border-b border-[#27272A] px-6 py-4 flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-[#FF331F]" />
              <div className="font-display uppercase tracking-widest text-sm">My payments</div>
            </div>
            <div className="divide-y divide-[#27272A]">
              {transactions.length === 0 ? (
                <div className="p-10 text-center text-zinc-500 text-sm">No payments yet.</div>
              ) : transactions.map((t) => (
                <div key={t.id} className="p-5 flex items-center justify-between" data-testid={`my-txn-${t.id}`}>
                  <div>
                    <div className="font-display uppercase text-sm">{t.package_name}</div>
                    <div className="text-xs text-zinc-500">{new Date(t.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-white">${t.amount?.toFixed(2)}</div>
                    <span className={`text-[10px] uppercase tracking-widest ${t.payment_status === "paid" ? "text-emerald-400" : "text-zinc-400"}`}>{t.payment_status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* PDF Library entitlements */}
        {libraryItems.length > 0 && (
          <div className="mt-6 border border-[#27272A] bg-[#0F0F0F]" data-testid="my-library-card">
            <div className="border-b border-[#27272A] px-6 py-4 flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-[#FF331F]" />
              <div className="font-display uppercase tracking-widest text-sm">My library</div>
            </div>
            <div className="divide-y divide-[#27272A]">
              {libraryItems.map((it) => {
                const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
                return (
                  <div key={`${it.resource_id}-${it.granted_at}`} className="p-5 flex items-center justify-between gap-3" data-testid={`my-library-${it.resource_slug}`}>
                    <div className="min-w-0">
                      <div className="font-display uppercase truncate">{it.resource_title}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">{it.granted_via === "purchase" ? "Purchased" : "Free download"} · {new Date(it.granted_at).toLocaleDateString()}</div>
                    </div>
                    {it.download_url && (
                      <a href={`${apiBase}${it.download_url}`} target="_blank" rel="noopener noreferrer"
                        className="shrink-0 inline-flex items-center gap-1 bg-[#FF331F] hover:bg-[#FF5242] text-white text-[11px] uppercase tracking-widest font-bold px-3 py-2 rounded-sm">
                        <Download className="h-3 w-3" /> Download
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      <Toaster position="top-right" theme="dark" />
    </div>
  );
}
